import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { BadgeComponent } from '../../../../../shared/components/ui/badge/badge.component';
import { AuthService } from '../../../../auth-pages/services/auth.service';
import { MeetingStatusApiService } from '../../../services/meeting-status-api.service';
import { MeetingStatusDrawerService } from '../../../services/meeting-status-drawer.service';
import { MeetingStatusResponse } from '../../../models/meeting.model';
import { ConfirmDialogService } from '../../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../../../shared/directives/has-permission.directive';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../../../shared/utils/pagination.constants';

@Component({
  selector: 'app-meeting-statuses-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    BadgeComponent,
    HasPermissionDirective,
  ],
  templateUrl: './meeting-statuses-list.component.html',
})
export class MeetingStatusesListComponent implements OnInit, OnDestroy {
  items: MeetingStatusResponse[] = [];
  loading = false;
  error = '';

  filtroSearch = '';
  filtroActive: '' | 'true' | 'false' = '';
  filtroFinal: '' | 'true' | 'false' = '';

  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;
  pageSize = 10;
  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private api: MeetingStatusApiService,
    private drawerService: MeetingStatusDrawerService,
    private authService: AuthService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    const savedPageSize = localStorage.getItem('meeting-statuses:pageSize');
    if (savedPageSize) this.pageSize = +savedPageSize;
    this.cargar();
    this.drawerService.saved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cargar());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get businessId(): number {
    return this.authService.getCurrentUser()?.businessId || 1;
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    this.api.list(this.businessId, this.globalPage, this.pageSize).subscribe({
      next: (page) => {
        let data = [...page.content];
        if (this.filtroSearch.trim()) {
          const term = this.filtroSearch.trim().toLowerCase();
          data = data.filter(
            (i) =>
              i.name.toLowerCase().includes(term) ||
              (i.label || '').toLowerCase().includes(term)
          );
        }
        if (this.filtroActive !== '') {
          const want = this.filtroActive === 'true';
          data = data.filter((i) => !!i.isActive === want);
        }
        if (this.filtroFinal !== '') {
          const want = this.filtroFinal === 'true';
          data = data.filter((i) => !!i.isFinal === want);
        }
        this.items = data.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        this.globalTotalPages = page.totalPages;
        this.globalTotalElements = page.totalElements;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error cargando estados';
        this.loading = false;
      },
    });
  }

  buscar(): void {
    this.globalPage = 0;
    this.cargar();
  }

  onPageSizeChange(): void {
    localStorage.setItem('meeting-statuses:pageSize', this.pageSize.toString());
    this.globalPage = 0;
    this.cargar();
  }

  goToPage(p: number): void {
    if (p < 0 || p >= this.globalTotalPages) return;
    this.globalPage = p;
    this.cargar();
  }

  nuevo(): void {
    this.drawerService.open();
  }

  editar(item: MeetingStatusResponse): void {
    this.drawerService.open({ statusId: item.id });
  }

  toggle(item: MeetingStatusResponse): void {
    this.api
      .update(item.id, {
        businessId: this.businessId,
        name: item.name,
        label: item.label,
        description: item.description,
        color: item.color,
        sortOrder: item.sortOrder,
        isFinal: item.isFinal,
        isDefault: item.isDefault,
        isSystem: item.isSystem,
        isActive: !item.isActive,
      })
      .subscribe({ next: () => this.cargar() });
  }

  async eliminar(item: MeetingStatusResponse): Promise<void> {
    if (item.isSystem) {
      await this.confirmDialog.alert({ title: 'Aviso', message: 'Los estados del sistema no se pueden eliminar.', variant: 'warning' });
      return;
    }
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar estado',
      message: `¿Eliminar estado "${item.label || item.name}"?`
    });
    if (!ok) return;
    this.api.delete(item.id, this.businessId).subscribe({
      next: () => { this.cargar(); this.toast.deleted('Estado de reunión', `Se eliminó "${item.label || item.name}" correctamente.`); },
      error: (err) => { this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar' }); },
    });
  }

  trackById = (_: number, item: MeetingStatusResponse) => item.id;
}
